import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ReactElement } from 'react';
import { Icon, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import * as SharedStyles from './PlanChangeConfirmation.styles';

const S = {
  ...SharedStyles,
  List: styled.ul`
    padding: 0;
    list-style: none;
    margin: ${tokens.spacer.x3} 0;
  `,
  ListItem: styled.li`
    display: flex;
    align-items: center;
    gap: ${tokens.spacer.x2};
    margin-bottom: ${tokens.spacer.x3};
  `,
  Icon: styled.span`
    padding: ${tokens.spacer.x3};
  `,
};

const ITEM_ICON = {
  upgrade: {
    icon: 'arrow_up',
    color: 'text_success',
    background: tokens.color.bg_success,
  },
  downgrade: {
    icon: 'arrow_down',
    color: 'text_danger',
    background: tokens.color.bg_danger,
  },
} as const;

interface PlanChangeItem {
  count: number;
  type: 'upgrade' | 'downgrade';
}

export interface PlanChangesProps {
  changes: PlanChangeItem[];
}

const PlanChangesItem = ({ count, type }: PlanChangeItem): ReactElement => {
  const { t } = useTranslationWithPrefix(`billing:plan-change-confirmation.plan.${type}`);
  const { icon, color, background } = ITEM_ICON[type];
  return (
    <S.ListItem>
      <S.Icon
        css={css`
          background: ${background};
        `}
      >
        <Icon icon={icon} color={color} size="l" />
      </S.Icon>
      <div>
        <S.Title>{t('title', { count })}</S.Title>
        <Text textType="body2" textColor="text_light">
          {t('description')}
        </Text>
      </div>
    </S.ListItem>
  );
};

export const PlanChanges = ({ changes }: PlanChangesProps): ReactElement => {
  const { t } = useTranslationWithPrefix('billing:plan-change-confirmation.plan');

  return (
    <div>
      <S.Title>{t('title')}</S.Title>
      <S.List>
        {changes.map(
          ({ count, type }) => !!count && <PlanChangesItem count={count} type={type} key={type} />,
        )}
      </S.List>
    </div>
  );
};
